import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './TeacherFreeTier.css';

/**
 * NotebookPanel — teacher dashboard section for the NotebookLM outcome loop.
 *
 * Create notebooks (or import from NotebookLM via the bookmarklet), add
 * sources, assign to students via share link, and see per-assignment
 * outcomes — the screen NotebookLM can't show.
 */

const BOOKMARKLET_HREF =
  "javascript:(function(){var t=(document.title||'').replace(/\\s*[-\\u2013|]\\s*NotebookLM.*$/i,'').trim()||'NotebookLM notebook';" +
  "var l=Array.prototype.map.call(document.querySelectorAll('a[href]'),function(a){return a.href})" +
  ".filter(function(h){return /youtube\\.com\\/watch|youtu\\.be\\//.test(h)});" +
  "l=l.filter(function(h,i){return l.indexOf(h)===i}).slice(0,10);" +
  "var s='';try{s=String(window.getSelection())}catch(e){}" +
  "var p={title:t,summary:s.slice(0,60000),youtube:l};" +
  "window.open('https://app.teachtappy.ai/notebook-import#'+btoa(unescape(encodeURIComponent(JSON.stringify(p)))),'_blank');})();";

const NotebookPanel = () => {
  const [notebooks, setNotebooks] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);       // notebook id with source form open
  const [outcomesFor, setOutcomesFor] = useState(null); // assignment id with outcomes open
  const [outcomes, setOutcomes] = useState([]);
  const [sourceDraft, setSourceDraft] = useState({ source_type: 'text', title: '', url: '', text: '' });
  const [notice, setNotice] = useState('');

  const refresh = useCallback(() => {
    axios.get('/api/notebooks').then((r) => setNotebooks(r.data.notebooks || [])).catch(() => {});
    axios.get('/api/notebooks/assignments').then((r) => setAssignments(r.data.assignments || [])).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 4000); };

  const createNotebook = async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/notebooks', { title: newTitle.trim() });
      if (r.data.success) { setNewTitle(''); refresh(); setExpanded(r.data.notebook_id); }
    } catch (err) {
      flash(err.response?.data?.error || 'Could not create notebook');
    } finally { setBusy(false); }
  };

  const addSource = async (notebookId) => {
    setBusy(true);
    try {
      const r = await axios.post(`/api/notebooks/${notebookId}/sources`, sourceDraft);
      if (r.data.success) {
        setSourceDraft({ source_type: 'text', title: '', url: '', text: '' });
        refresh();
        flash(`Source added (${r.data.source.char_count.toLocaleString()} characters).`);
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Could not add source');
    } finally { setBusy(false); }
  };

  const assignNotebook = async (notebookId) => {
    setBusy(true);
    try {
      const r = await axios.post(`/api/notebooks/${notebookId}/assignments`, {});
      if (r.data.success) {
        refresh();
        try { await navigator.clipboard.writeText(r.data.assignment_url); } catch (e) {}
        flash('Assignment created — link copied to clipboard. Share it with your students.');
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Could not create assignment');
    } finally { setBusy(false); }
  };

  const showOutcomes = async (assignmentId) => {
    if (outcomesFor === assignmentId) { setOutcomesFor(null); return; }
    try {
      const r = await axios.get(`/api/notebooks/assignments/${assignmentId}/outcomes`);
      setOutcomes(r.data.outcomes || []);
      setOutcomesFor(assignmentId);
    } catch (e) { flash('Could not load outcomes'); }
  };

  return (
    <section className="teacher-lite-section">
      <h2 className="teacher-lite-subheading">Class materials &amp; assignments</h2>
      <p className="teacher-lite-subhead">
        Bring in your unit materials — from NotebookLM or straight upload — assign them,
        and see exactly how each student did.
      </p>

      {notice && <div className="teacher-step-hint">{notice}</div>}

      {/* Create + bookmarklet */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '12px 0' }}>
        <input
          className="teacher-input"
          style={{ flex: 1, minWidth: 200 }}
          placeholder="New notebook title (e.g. Unit 3: Fractions)"
          value={newTitle}
          maxLength={300}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <button className="teacher-btn-primary" onClick={createNotebook} disabled={busy || !newTitle.trim()}>
          Create
        </button>
      </div>
      <p className="teacher-lite-empty-hint">
        Use NotebookLM? Drag this to your bookmarks bar, then click it on any notebook page:{' '}
        {/* eslint-disable-next-line no-script-url */}
        <a href={BOOKMARKLET_HREF} onClick={(e) => e.preventDefault()}
           style={{ fontWeight: 600, color: '#297A92' }}>
          📔 Push to Tappy
        </a>
        {' '}(select the notebook summary text first to bring it along)
      </p>

      {/* Notebook list */}
      {notebooks.map((nb) => (
        <div key={nb.id} className="teacher-invite-panel" style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <strong>{nb.title}</strong>
              <span className="teacher-lite-empty-hint" style={{ marginLeft: 8 }}>
                {nb.source_count} source{nb.source_count === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="teacher-btn-secondary" onClick={() => setExpanded(expanded === nb.id ? null : nb.id)}>
                {expanded === nb.id ? 'Close' : 'Add source'}
              </button>
              <button className="teacher-btn-primary" onClick={() => assignNotebook(nb.id)}
                      disabled={busy || nb.source_count === 0}
                      title={nb.source_count === 0 ? 'Add at least one source first' : ''}>
                Assign →
              </button>
            </div>
          </div>

          {expanded === nb.id && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {['text', 'url', 'youtube'].map((t) => (
                  <button key={t}
                          className={sourceDraft.source_type === t ? 'teacher-btn-primary' : 'teacher-btn-secondary'}
                          onClick={() => setSourceDraft({ ...sourceDraft, source_type: t })}>
                    {t === 'text' ? 'Paste text' : t === 'url' ? 'Web page' : 'YouTube'}
                  </button>
                ))}
              </div>
              <input className="teacher-input" placeholder="Source name (e.g. Chapter 4 notes)"
                     value={sourceDraft.title} style={{ marginBottom: 8 }}
                     onChange={(e) => setSourceDraft({ ...sourceDraft, title: e.target.value })} />
              {sourceDraft.source_type === 'text' ? (
                <textarea className="teacher-input" rows={5} placeholder="Paste your material here…"
                          value={sourceDraft.text}
                          onChange={(e) => setSourceDraft({ ...sourceDraft, text: e.target.value })} />
              ) : (
                <input className="teacher-input"
                       placeholder={sourceDraft.source_type === 'youtube' ? 'YouTube video URL' : 'Web page URL'}
                       value={sourceDraft.url}
                       onChange={(e) => setSourceDraft({ ...sourceDraft, url: e.target.value })} />
              )}
              <div className="teacher-step-actions" style={{ marginTop: 10 }}>
                <button className="teacher-btn-primary" onClick={() => addSource(nb.id)} disabled={busy}>
                  {busy ? 'Adding…' : 'Add source'}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Assignments + outcomes */}
      {assignments.length > 0 && (
        <>
          <h3 className="teacher-lite-subheading" style={{ marginTop: 20 }}>Assignments</h3>
          {assignments.map((a) => (
            <div key={a.id} className="teacher-lite-student-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="teacher-lite-student-name">
                {a.title || a.notebook_title}
                <span className="teacher-lite-student-stats" style={{ marginLeft: 10 }}>
                  {a.students_completed} student{a.students_completed === 1 ? '' : 's'} completed
                  {a.avg_mastery != null && ` · avg mastery ${(a.avg_mastery * 100).toFixed(0)}%`}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="teacher-btn-secondary"
                        onClick={async () => {
                          const url = `${window.location.origin}/assignment/${a.share_token}`;
                          try { await navigator.clipboard.writeText(url); flash('Link copied.'); } catch (e) {}
                        }}>
                  Copy link
                </button>
                <button className="teacher-btn-secondary" onClick={() => showOutcomes(a.id)}>
                  {outcomesFor === a.id ? 'Hide outcomes' : 'Outcomes'}
                </button>
              </div>
              {outcomesFor === a.id && (
                <div style={{ width: '100%', marginTop: 8 }}>
                  {outcomes.length === 0 ? (
                    <p className="teacher-lite-empty-hint">No sessions yet — share the link with your class.</p>
                  ) : (
                    outcomes.map((o, i) => (
                      <div key={i} className="teacher-lite-preview-row">
                        <span>{o.student_name}</span>
                        <span className="teacher-lite-student-stats">
                          {o.mastery != null ? `mastery ${(o.mastery * 100).toFixed(0)}%` : 'in progress'}
                          {o.questions_count != null && ` · ${o.questions_count} questions`}
                          {o.duration_seconds != null && ` · ${Math.round(o.duration_seconds / 60)} min`}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </section>
  );
};

export default NotebookPanel;
